import React from "react"
import { useDrop } from "react-dnd"
import { Box, Stack, Typography, Avatar, Paper, Chip } from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import BuildIcon from "@mui/icons-material/Build"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import { useTranslation } from "react-i18next"
import { ItemTypes, useVisualEditor } from "../VisualEditorContext"

const StepPreviewCard = ({ step, index, onClick }) => {
  const { t } = useTranslation()

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 2,
        border: "1px solid #e2e8f0",
        borderRadius: 2,
        bgcolor: "#ffffff",
        cursor: "pointer",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: "#3b82f6",
          bgcolor: "#f8fafc",
          transform: "translateX(4px)",
        },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar
          sx={{
            bgcolor: "#3b82f6",
            color: "#ffffff",
            width: 28,
            height: 28,
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {step.seq}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight={600} color="#1e293b">
            {step.typid}
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
            <Chip
              size="small"
              icon={<BuildIcon sx={{ fontSize: 10 }} />}
              label={`${step.materials?.length || 0} ${t("materials")}`}
              sx={{
                height: 20,
                fontSize: "0.7rem",
                bgcolor: step.materials?.length > 0 ? "#dbeafe" : "#f1f5f9",
                color: step.materials?.length > 0 ? "#3b82f6" : "#64748b",
                "& .MuiChip-icon": {
                  color: step.materials?.length > 0 ? "#3b82f6" : "#64748b",
                },
              }}
            />
            {step.instructions && (
              <Chip
                size="small"
                label={t("Has instructions")}
                sx={{
                  height: 20,
                  fontSize: "0.7rem",
                  bgcolor: "#dcfce7",
                  color: "#16a34a",
                }}
              />
            )}
          </Stack>
        </Box>
        <ArrowForwardIcon sx={{ color: "#94a3b8", fontSize: 18 }} />
      </Stack>
    </Paper>
  )
}

const EmptyState = () => {
  const { t } = useTranslation()

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 8,
        color: "#94a3b8",
      }}
    >
      <AddIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
      <Typography variant="body1" fontWeight={500} color="#64748b">
        {t("Drop sections here")}
      </Typography>
      <Typography variant="body2" color="#94a3b8" sx={{ mt: 0.5 }}>
        {t("Drag sections from the right panel to create production steps")}
      </Typography>
    </Box>
  )
}

const DroppableCanvas = () => {
  const { t } = useTranslation()
  const { steps, addStep, setActiveTab } = useVisualEditor()

  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: ItemTypes.SECTION,
    drop: (item) => {
      addStep(item.section.name)
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }), [addStep])

  const isActive = isOver && canDrop

  const handleStepClick = (stepIndex) => {
    setActiveTab(1) // Switch to Steps tab
  }

  return (
    <Box
      ref={drop}
      sx={{
        minHeight: 400,
        p: 2,
        borderRadius: 3,
        border: isActive ? "2px dashed #3b82f6" : "2px dashed #e2e8f0",
        bgcolor: isActive ? "#eff6ff" : "#f8fafc",
        transition: "all 0.2s ease",
      }}
    >
      {steps.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <Typography variant="caption" color="#64748b" sx={{ mb: 1.5, display: "block" }}>
            {t("Click on a step to edit materials")}
          </Typography>
          <Stack spacing={1.5}>
            {steps.map((step, index) => (
              <StepPreviewCard
                key={index}
                step={step}
                index={index}
                onClick={() => handleStepClick(index)}
              />
            ))}
          </Stack>
        </>
      )}
    </Box>
  )
}

export default DroppableCanvas
